import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { StyleSheet, Keyboard } from 'react-native';
import Alert from 'utils/packages/Alert';
import emoji from 'node-emoji';
import { linkNumberRequestCode } from 'actions';
import { selectFeatureFlag } from 'reducers/selectors';
import { useAppSelector } from 'store/hooks';
import PhoneNumberInputView from 'design-system/PhoneNumberInputView';
import { muteErrorAndResult, useFetchOld } from 'hooks';
import useColors from 'hooks/useColors';
import { AppView, Spacer } from 'design-system';
import StatusBar from 'design-system/StatusBar';
import { KeyboardAvoidingContainerNew } from 'design-system/KeyboardAvoidingContainerNew';
import { KeyboardAvoidingButton } from 'design-system/KeyboardAvoidingButton';
import { showConfirmPhoneNumberModal } from 'utils/confirmPhoneNumberModal';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { countryCode as defaultCountryCode } from '../../../constants';
import prefixes from '../../../login/sms/prefixes';
export const PhoneVerificationScreen = ({ navigation, route, }) => {
    const colors = useColors();
    useLayoutEffect(() => {
        navigation.setOptions({
            animationTypeForReplace: 'push',
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const textInput = useRef(null);
    const [state, setState] = useState(() => {
        let code = 'GB';
        let prefix = '+44';
        const countryCode = route.params.countryCode ?? defaultCountryCode;
        const foundPrefix = Object.values(prefixes).find((country) => country.iso2 === countryCode);
        if (foundPrefix) {
            code = foundPrefix.iso2;
            prefix = foundPrefix.prefix;
        }
        return {
            phoneNumber: null,
            selectedCountry: { prefix, code },
        };
    });
    const [, , isSendingNumber, fetch] = useFetchOld(muteErrorAndResult);
    const phoneConfirmationEnabled = useAppSelector((store) => selectFeatureFlag(store, 'phone_number_confirmation').value);
    const timeoutRef = useRef(undefined);
    useEffect(() => () => {
        clearTimeout(timeoutRef.current);
    }, []);
    const openNext = () => {
        Keyboard.dismiss();
        const invalid = !state.phoneNumber || !state.phoneNumber.length;
        const { phoneNumber, selectedCountry } = state;
        if (invalid || !phoneNumber) {
            timeoutRef.current = setTimeout(() => {
                Alert.alert(emoji.emojify('Hold on :dizzy:'), 'You must enter a phone number to continue.', [
                    {
                        text: 'OK',
                        onPress: () => textInput.current?.focus(),
                    },
                ]);
            }, 50);
            return;
        }
        if (!phoneConfirmationEnabled) {
            sendCode();
            return;
        }
        showConfirmPhoneNumberModal({
            countryCode: selectedCountry.code,
            prefix: selectedCountry.prefix,
            phoneNumber,
            onSendCode: sendCode,
            onEditNumber: () => textInput.current?.focus(),
        });
    };
    const sendCode = async () => {
        const { phoneNumber, selectedCountry } = state;
        if (!phoneNumber) {
            return;
        }
        const { prefix } = selectedCountry;
        const response = await fetch(linkNumberRequestCode(phoneNumber, prefix.substr(1), 'sms'));
        if (response) {
            navigation.navigateDeprecated('PhoneVerificationConfirm', {
                flow: route.params.flow,
                phoneNumber,
                prefix: prefix.substr(1),
                onSuccess: route.params.onSuccess,
            });
        }
    };
    const onChangeText = (data) => {
        setState((state) => ({ ...state, phoneNumber: data }));
    };
    const onSelectedPrefix = (data) => {
        const { item } = data;
        const { prefix, iso2 } = item;
        setState((state) => ({
            ...state,
            selectedCountry: { code: iso2, prefix },
        }));
        textInput.current?.focus();
    };
    const { selectedCountry } = state;
    const { code, prefix } = selectedCountry;
    return (<AppView type="primary" style={styles.content} avoidHeader>
      <StatusBar barStyle="dark-content" animated/>
      <ScreenInfoHeader style={styles.screenInfoHeader} title={route.params.title ?? 'Enter your phone number'} subTitle={route.params.subTitle ??
            'We need your phone number for regulatory requirements'}/>
      <KeyboardAvoidingContainerNew flexContainer withButtonSpace>
        <PhoneNumberInputView textInputRef={textInput} code={code} prefix={prefix} onChangeText={onChangeText} onSelectedPrefix={onSelectedPrefix} onSubmitEditing={openNext} value={state.phoneNumber}/>
        {route.params.title ? <Spacer h={24}/> : null}
      </KeyboardAvoidingContainerNew>
      <KeyboardAvoidingButton brand title="Continue" withFrame={false} onPress={openNext} isFetching={isSendingNumber}/>
    </AppView>);
};
const styles = StyleSheet.create({
    content: {
        flex: 1,
        overflow: 'hidden',
    },
    screenInfoHeader: {
        paddingBottom: 0,
    },
});
